import React, { Fragment, useId, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { PlusOutlined } from '@ant-design/icons'

import { ExtendedTable, SubheaderToolbarDivider } from '@app/components'
import { TableSearchButton } from '@app/components/TableSettingsToolbar'
import { AddPriceRestorationDrawer } from '@app/drawers'
import { FileUploadModal, ACCEPT_FILE_TYPES } from '@app/modals/FileUploadModal'
import { LntButton, LntDrawer, LntForm, LntFormItem, LntInput } from '@app/components/base'

import { MONITORING_VALIDATION_KEY } from '@app/constants/queryKeys'
import { BUTTON_TYPE, SIZE } from '@app/constants'
import { FILE_TYPES } from '@app/api/constants'

import { isEmptyObject } from '@app/utils/object'
import { exportMonitoringValidationStoresLinksFile } from '@app/utils/export'
import { getMonitoringValidationConfigStoresLinksDataFromResponse } from '@app/utils/responseAdapters'

import { useNotification, usePaginationParams, useRemoveTableRowButton, useToggle } from '@app/hooks'

import {
  deleteMonitoringValidationConfigStoresLinks,
  getMonitoringValidationConfigStoresLinks,
  getMonitoringValidationPriceRestorationConfig,
  importMonitoringValidationStoresLinks,
  patchMonitoringValidationPriceRestorationConfig
} from '@app/api/routes/monitoringValidation'

import { isNull } from '@app/utils'

import { getColumns } from './utils'

import styles from './styles.module.scss'

import type { PriceRestorationDrawerProps } from './types'
import type { MonitoringValidationConfigStoresLinksDataSource } from '@app/types/dataSource'
import type { PatchMonitoringValidationPriceRestorationConfigData } from '@app/api/routes/monitoringValidation/types'

const columns = getColumns()

const title = 'Восстановление цен'

export const PriceRestorationDrawer: React.FC<PriceRestorationDrawerProps> = props => {
  const { onClose } = props

  const addPriceRestorationDrawerToggle = useToggle()
  const importFromExcelModalToggle = useToggle()

  const { pagination, setPagination, paginationParams } = usePaginationParams()

  const formId = useId()
  const form = useForm<PatchMonitoringValidationPriceRestorationConfigData>()
  const queryClient = useQueryClient()
  const notification = useNotification()

  const { data: configData } = useQuery({
    queryKey: [MONITORING_VALIDATION_KEY.priceRestoration],
    queryFn: async () => await getMonitoringValidationPriceRestorationConfig()
  })

  const { data: monitoringValidationStoresLinksData, isLoading: isMonitoringValidationStoresLinksDataLoading } =
    useQuery({
      queryKey: [MONITORING_VALIDATION_KEY.storesLinks],
      queryFn: async () => await getMonitoringValidationConfigStoresLinks(paginationParams)
    })

  const deleteMonitoringValidationStoresLinksMutation = useMutation({
    mutationFn: deleteMonitoringValidationConfigStoresLinks,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [MONITORING_VALIDATION_KEY.storesLinks] })

      notification.success({
        message: 'Выбранная связь успешно удалена.'
      })
    }
  })

  const patchConfigDataMutation = useMutation({
    mutationFn: patchMonitoringValidationPriceRestorationConfig,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [MONITORING_VALIDATION_KEY.priceRestoration] })

      notification.success({
        message: 'Успешно изменен период обращения в историю.'
      })

      onClose?.()
    }
  })

  const dataSource = useMemo(
    () => getMonitoringValidationConfigStoresLinksDataFromResponse(monitoringValidationStoresLinksData),
    [monitoringValidationStoresLinksData]
  )

  const handlePaginationChange = (current: number, pageSize: number): void => setPagination({ current, pageSize })

  const handleExportStoresLinksFile = (): void => {
    const fileName = 'Список_связей_магазинов'

    exportMonitoringValidationStoresLinksFile(fileName, { file: FILE_TYPES.xlsx })
  }

  const handleImportFromExcel = async (file: File | null): Promise<void> => {
    if (isNull(file)) return

    const pricesFormData = new FormData()
    pricesFormData.append('file', file, file.name)
    pricesFormData.append('fileFormat', FILE_TYPES.xlsx)

    await importMonitoringValidationStoresLinks(pricesFormData)

    await queryClient.invalidateQueries({ queryKey: [MONITORING_VALIDATION_KEY.storesLinks] })

    importFromExcelModalToggle.setUntoggled()
  }

  const handleDeleteStoresLinks = (rowKey: string): void => {
    deleteMonitoringValidationStoresLinksMutation.mutate({ linkId: rowKey })
  }

  const handleSubmit = (data: PatchMonitoringValidationPriceRestorationConfigData): void => {
    if (!data) return

    patchConfigDataMutation.mutate(data)
  }

  useRemoveTableRowButton({
    columns,
    onClick: rowKey => handleDeleteStoresLinks(String(rowKey))
  })

  const footerButtons = (
    <div className={ styles.priceRestorationDrawer__footer }>
      <LntButton
        htmlType='submit'
        form={ formId }
        disabled={ isEmptyObject(form.formState.dirtyFields) }
      >
        Сохранить
      </LntButton>
    </div>
  )

  const toolbar = [
    <Fragment key='price-restoration-contol-buttons'>
      <LntButton
        type={ BUTTON_TYPE.text }
        size={ SIZE.small }
        onClick={ importFromExcelModalToggle.setToggled }
      >
        Импорт
      </LntButton>

      <LntButton
        type={ BUTTON_TYPE.text }
        size={ SIZE.small }
        onClick={ handleExportStoresLinksFile }
      >
        Экспорт
      </LntButton>

      <LntButton
        icon={ <PlusOutlined /> }
        type={ BUTTON_TYPE.primary }
        size={ SIZE.small }
        onClick={ addPriceRestorationDrawerToggle.setToggled }
      >
        Связь
      </LntButton>

      <SubheaderToolbarDivider />
    </Fragment>,

    // TODO: Добавить табличный фильтр
    <TableSearchButton key='search-clusters' />
  ]

  return (
    <LntDrawer
      { ...props }
      title={ title }
      width={ 1080 }
      footer={ footerButtons }
    >
      <LntForm<PatchMonitoringValidationPriceRestorationConfigData>
        layout='horizontal'
        className={ styles.priceRestorationDrawer__form }
        formInstance={ form }
        onSubmit={ handleSubmit }
        id={ formId }
      >
        <div className={ styles.priceRestorationDrawer__formTop }>
          <LntFormItem
            name='priceRestorationHistoricalDepth'
            defaultValue={ configData?.priceRestorationHistoricalDepth }
            label='Период обращения в историю цен'
          >
            <LntInput
              className={ styles.priceRestorationDrawerFormTop__inputDays }
              placeholder='Кол-во дней'
              type='number'
            />
          </LntFormItem>
        </div>

        <ExtendedTable<MonitoringValidationConfigStoresLinksDataSource>
          loading={ isMonitoringValidationStoresLinksDataLoading }
          toolbar={ toolbar }
          columns={ columns }
          dataSource={ dataSource }
          pagination={ {
            ...pagination,
            total: monitoringValidationStoresLinksData?.count,
            onChange: handlePaginationChange
          } }
        />
      </LntForm>

      <FileUploadModal
        title='Импорт из excel'
        accept={ ACCEPT_FILE_TYPES.xlsx }
        open={ importFromExcelModalToggle.value }
        onCancel={ importFromExcelModalToggle.setUntoggled }
        onFileUpload={ handleImportFromExcel }
      />

      <AddPriceRestorationDrawer
        open={ addPriceRestorationDrawerToggle.value }
        onClose={ addPriceRestorationDrawerToggle.setUntoggled }
      />
    </LntDrawer>
  )
}
